import React from 'react';

import classNames from 'classnames';

import { FxBaseCurrencyNote, FxRate, withIncomplete } from '../ShowInSelect';

import { formatMoney, formatPercent, totalLabel, ZERO_DASH } from './budgetOverviewFormat';
import BudgetOverviewInfoTip from './BudgetOverviewInfoTip';

import Badge from 'CommonComponents/Badge';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

// On an inner wrapper rather than the cell: the table's own cell padding
// outranks a utility class on the <td>.
const LEVEL_INDENT = ['', 'tw-ps-6', 'tw-ps-12'];

function Toggle({ open, label, onClick }) {
    const Icon = open ? ChevronDownIcon : ChevronRightIcon;

    return (
        <button type="button" onClick={onClick} aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`} className="tw-inline-flex">
            <Icon className="tw-w-2.5 tw-h-2.5" />
        </button>
    );
}

/**
 * The label of a row: indented one step per level, with a fixed-width slot
 * for the expand control so a name lines up whether or not its row has one.
 */
function RowLabel({ level, toggle = null, className, children }) {
    return (
        <div className={classNames('tw-flex tw-items-center', LEVEL_INDENT[level], className)}>
            <span className="tw-inline-flex tw-w-4 tw-shrink-0 tw-items-center">{toggle}</span>
            {children}
        </div>
    );
}

function Money({ value, locale }) {
    return <span className={classNames({ 'tw-text-red': value < 0 })}>{formatMoney(value, locale)}</span>;
}

function MarginCell({ row, locale }) {
    return (
        <td className="inbud-numeric-cell">
            <div>{formatMoney(row.margin_value, locale)}</div>
            <div className="tw-text-xs tw-text-charcoal-50">{formatPercent(row.margin_pct, locale)}</div>
        </td>
    );
}

/**
 * The figure cells of one row. `markups` is null on a project without mark-ups
 * ("No mark-ups" on the project row, a dash beneath it); revenue belongs to the
 * project row only.
 */
function FigureCells({ row, level, locale, columns, isTotal = false }) {
    const isProject = level === 0;
    const carriesRevenue = isProject || isTotal;

    if (isProject && !row.has_budget) {
        return (
            <>
                <td className="inbud-numeric-cell tw-text-charcoal-50">No budget</td>
                <td className="inbud-numeric-cell">{ZERO_DASH}</td>
                <td className="inbud-numeric-cell">{ZERO_DASH}</td>
                <td className="inbud-numeric-cell">{ZERO_DASH}</td>
                {columns.revenue && <td className="inbud-numeric-cell">{ZERO_DASH}</td>}
                {columns.committedActuals && (
                    <>
                        <td className="inbud-numeric-cell">{ZERO_DASH}</td>
                        <td className="inbud-numeric-cell">{ZERO_DASH}</td>
                        <td className="inbud-numeric-cell">{ZERO_DASH}</td>
                    </>
                )}
            </>
        );
    }

    let markups = formatMoney(row.markups, locale);
    if (row.markups === null && isProject) {
        markups = <span className="tw-text-charcoal-50">No mark-ups</span>;
    }

    return (
        <>
            <td className="inbud-numeric-cell">
                <Money value={row.budget} locale={locale} />
            </td>
            <td className="inbud-numeric-cell">{markups}</td>
            <td className="inbud-numeric-cell">
                <Money value={row.total} locale={locale} />
            </td>
            <MarginCell row={row} locale={locale} />
            {columns.revenue && <td className="inbud-numeric-cell">{carriesRevenue && row.revenue !== null ? formatMoney(row.revenue, locale) : ZERO_DASH}</td>}
            {columns.committedActuals && (
                <>
                    <td className="inbud-numeric-cell">
                        <Money value={row.committed} locale={locale} />
                    </td>
                    <td className="inbud-numeric-cell">
                        <Money value={row.actuals} locale={locale} />
                    </td>
                    <td className="inbud-numeric-cell tw-font-semibold">
                        <Money value={row.remaining} locale={locale} />
                    </td>
                </>
            )}
        </>
    );
}

function ProjectRows({ project, columns, converting, expandedProjects, expandedDepartments, onToggleProject, onToggleDepartment }) {
    const locale = project.locale;
    const expandable = project.has_budget && project.departments.length > 0;
    const open = expandable && expandedProjects.has(project.id);

    return (
        <>
            <tr>
                <td className="tw-text-start">
                    <RowLabel level={0} toggle={expandable ? <Toggle open={open} label={project.name} onClick={() => onToggleProject(project.id)} /> : null}>
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                            <span className="tw-font-semibold">{project.name}</span>
                            {project.currency && <span className="tw-text-xs tw-text-charcoal-50">{project.currency}</span>}
                            <Badge variant="neutral">{project.version}</Badge>
                            <FxBaseCurrencyNote fx={project.fx} />
                        </div>
                    </RowLabel>
                </td>
                {converting && (
                    <td className="inbud-numeric-cell">
                        <FxRate fx={project.fx} />
                    </td>
                )}
                <FigureCells row={project} level={0} locale={locale} columns={columns} />
                <td className="tw-text-end">
                    <a href={project.open_url} className="tw-font-semibold tw-text-blue hover:tw-text-blue-75">
                        Open
                    </a>
                </td>
            </tr>

            {open &&
                project.departments.map((department) => {
                    const key = department.id ?? `unassigned-${project.id}`;
                    const departmentExpandable = department.id !== null && department.costcenters.length > 0;
                    const departmentOpen = departmentExpandable && expandedDepartments.has(department.id);

                    return (
                        <React.Fragment key={key}>
                            <tr className="tw-bg-charcoal-10">
                                <td className="tw-text-start">
                                    <RowLabel
                                        level={1}
                                        className="tw-font-medium"
                                        toggle={
                                            departmentExpandable ? (
                                                <Toggle open={departmentOpen} label={department.name} onClick={() => onToggleDepartment(department.id)} />
                                            ) : null
                                        }
                                    >
                                        {department.name}
                                    </RowLabel>
                                </td>
                                {converting && <td />}
                                <FigureCells row={department} level={1} locale={locale} columns={columns} />
                                <td />
                            </tr>

                            {departmentOpen &&
                                department.costcenters.map((costcenter) => (
                                    <tr key={`${key}-${costcenter.id ?? 'unassigned'}`} className="tw-text-charcoal-75">
                                        <td className="tw-text-start">
                                            <RowLabel level={2}>{costcenter.name}</RowLabel>
                                        </td>
                                        {converting && <td />}
                                        <FigureCells row={costcenter} level={2} locale={locale} columns={columns} />
                                        <td />
                                    </tr>
                                ))}
                        </React.Fragment>
                    );
                })}
        </>
    );
}

function HeaderCell({ definition, numeric = true }) {
    return (
        <th className={numeric ? '!tw-text-end' : '!tw-text-start'}>
            <span className="tw-inline-flex tw-items-center tw-gap-1">
                {definition.label}
                <BudgetOverviewInfoTip label={definition.label} text={definition.text} />
            </span>
        </th>
    );
}

export default function BudgetOverviewTable({
    report,
    fx,
    definitions,
    columns,
    locale,
    expandedProjects,
    expandedDepartments,
    onToggleProject,
    onToggleDepartment,
}) {
    const totals = report.totals;
    const { label, suffix } = totalLabel(report);
    const converting = !!fx;
    const columnCount = 6 + (converting ? 1 : 0) + (columns.revenue ? 1 : 0) + (columns.committedActuals ? 3 : 0);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="!tw-text-start">Project</th>
                        {converting && (
                            <HeaderCell
                                definition={{
                                    label: $t('Rate'),
                                    text: $t(
                                        "Each project's rate against the anchor currency, common or locked. Figures are converted through the anchor when the report is drawn.",
                                    ),
                                }}
                            />
                        )}
                        <HeaderCell definition={definitions.budget} />
                        <HeaderCell definition={definitions.markups} />
                        <HeaderCell definition={definitions.total} />
                        <HeaderCell definition={definitions.margin} />
                        {columns.revenue && <HeaderCell definition={definitions.revenue} />}
                        {columns.committedActuals && (
                            <>
                                <HeaderCell definition={definitions.committed} />
                                <HeaderCell definition={definitions.actuals} />
                                <HeaderCell definition={definitions.remaining} />
                            </>
                        )}
                        <th className="tw-text-end" />
                    </tr>
                </thead>
                <tbody>
                    {report.projects.length === 0 && (
                        <tr>
                            <td colSpan={columnCount} className="tw-text-center tw-text-charcoal-75">
                                No selected projects match this status.
                            </td>
                        </tr>
                    )}
                    {report.projects.map((project) => (
                        <ProjectRows
                            key={project.id}
                            project={project}
                            columns={columns}
                            converting={converting}
                            expandedProjects={expandedProjects}
                            expandedDepartments={expandedDepartments}
                            onToggleProject={onToggleProject}
                            onToggleDepartment={onToggleDepartment}
                        />
                    ))}
                </tbody>
                {report.projects.length > 0 && (
                    <tfoot>
                        <tr>
                            <td className="tw-text-start">
                                {withIncomplete(
                                    <>
                                        {label} {suffix && <span className="tw-font-normal">{suffix}</span>}
                                    </>,
                                    fx,
                                )}
                            </td>
                            {converting && <td />}
                            <FigureCells row={{ ...totals, has_budget: true }} level={1} locale={locale} columns={columns} isTotal />
                            <td />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
